// Make partial streaming markdown safer to render
export const stabilizeMarkdownForStreaming = (text) => {
  if (!text) return '';
  let out = String(text);
  // Ensure trailing newline to avoid partial constructs at EOF
  if (!out.endsWith('\n')) out += '\n';
  // Close unmatched fenced code blocks (```)
  const fenceCount = (out.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) {
    out += '\n```\n';
  }
  // If GFM table pipes are present but no newlines to close a row yet,
  // append a newline to prevent table state from remaining open
  if (/\|/.test(out) && !/\n$/.test(out)) {
    out += '\n';
  }
  return out;
};

// Normalize single-line inline GFM tables into proper multi-line blocks
export const normalizeInlineGfmTables = (text) => {
  if (!text) return '';
  let out = String(text);
  const lines = out.split('\n');
  const result = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const fence = /^\s*```/.test(line);
    if (fence) {
      inFence = !inFence;
      result.push(line);
      continue;
    }
    if (inFence) {
      result.push(line);
      continue;
    }
    // If header, divider, and first row are squashed in one line like:
    // | h1 | h2 | h3 | |---|---|---| | r1c1 | r1c2 | r1c3 |
    // restore newlines before divider and each subsequent row
    if (/^\s*\|/.test(line)) {
      line = line.replace(/\|\s+\|(?=\s*[^|\s])/g, '|\n|');
    }
    result.push(line);
  }
  return result.join('\n');
};

// GFM-friendly utilities
const isGfmDividerRow = (line) => {
  const cleaned = String(line || '').trim();
  if (!/\|/.test(cleaned)) return false;
  const cells = cleaned
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
  if (cells.length < 2) return false;
  return cells.every((c) => /^:?-{3,}:?$/.test(c));
};

// During streaming, mask incomplete GFM tables by escaping pipes until a proper divider row is present.
export const maskIncompleteGfmTables = (text) => {
  const lines = String(text || '').split('\n');
  const out = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const fenceMatch = line.match(/^\s*```/);
    if (fenceMatch) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    const hasPipes = line.includes('|');
    if (!hasPipes) {
      out.push(line);
      continue;
    }
    // Potential header if next line exists and is divider
    const next = lines[i + 1] || '';
    const looksLikeHeader = hasPipes;
    if (looksLikeHeader && isGfmDividerRow(next)) {
      out.push(line);
      // keep divider row as-is
      out.push(next);
      i += 1;
      // Copy following rows as-is until blank line or no pipes
      let j = i + 1;
      while (j < lines.length) {
        const row = lines[j];
        if (row.trim() === '' || !row.includes('|')) break;
        out.push(row);
        j++;
      }
      i = j - 1;
      continue;
    }
    // Otherwise, escape pipes to prevent accidental GFM table parsing mid-stream
    out.push(line.replace(/\|/g, '\\|'));
  }
  return out.join('\n');
};
